import React, { useState, useEffect, useCallback } from 'react'
import { getJson, getText } from '../../../utils/http'
import { SearchCalendar } from './SearchCalendar'
import { searchData, PlanType } from './type'
import { PlanList } from './PlanList'
import { buildCheckInDateParams } from './utils'
import { useTranslation } from 'react-i18next'
import { useLpAvailabilityPageStore } from '../../../store/lpAvailabilityPageStore'

export const LpAvailability = () => {
  const { t } = useTranslation()
  const [planList, setPlanList] = useState<PlanType[]>([])
  const [loading, setLoading] = useState(false)
  const { isJpe, isMobileScreen } = useLpAvailabilityPageStore()

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      const queryParams = Object.fromEntries(new URLSearchParams(location.search).entries())
      const params = {
        ...buildCheckInDateParams(location.search, searchData),
        // 左プラン一覧では start_date / select_month も送る (Plan eligibility と月絞り込み用)
        start_date: queryParams.start_date || '',
        ...(queryParams.select_month ? { select_month: queryParams.select_month } : {}),
      }
      Object.assign(searchData, { ...searchData, ...queryParams })

      const data = await getJson(`${location?.pathname}.json`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        params,
      })

      setPlanList(data.plans)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    if (isJpe) return

    getText(location.pathname + '/header_price' + location.search)
      .then((html) => {
        if (html) {
          const element = document.getElementsByClassName('price-info-block')[0] as HTMLElement
          element.outerHTML = html
        }
        return
      })
      .catch(() => {})
  }, [location.search])

  return (
    <div className="flex flex-col-reverse md:flex-row content-new md:gap-80 justify-center">
      <div className="info-block">
        {!isMobileScreen && isJpe && (
          <div className="sticky top-0 z-10 bg-white py-2" style={{ marginLeft: '-5px' }}>
            <a href={location.pathname.replace('/availability', '')} data-turbo="false">
              <button
                className="text-gray-500 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-2"
                type="button"
              >
                <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.825 9L9.425 14.6L8 16L0 8L8 0L9.425 1.4L3.825 7H16V9H3.825Z" fill="black"></path>
                </svg>
              </button>
            </a>
          </div>
        )}
        <div id="availability">
          <p className="availability-title text-left font-a1 font-bold font-size-20 md:font-size-26 hidden md:block">
            {t('plan_list.component.button.available_date')}
          </p>
          {loading ? (
            <div className="flex px-4 flex-col">
              <PlanSkeleton />
              <PlanSkeleton />
            </div>
          ) : (
            <PlanList planList={planList} />
          )}
        </div>
      </div>
      <div className="conRight w-full md:w-[35%]">
        {isMobileScreen && isJpe && (
          <div className="sticky top-0 z-10 bg-white py-3 px-2">
            <a href={location.pathname.replace('/availability', '')} data-turbo="false">
              <button
                className="text-gray-500 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-2"
                type="button"
                style={{}}
              >
                <svg fill="none" height="16" viewBox="0 0 16 16" width="16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.825 9L9.425 14.6L8 16L0 8L8 0L9.425 1.4L3.825 7H16V9H3.825Z" fill="black"></path>
                </svg>
              </button>
            </a>
          </div>
        )}
        <p className="availability-title text-left font-a1 font-bold font-size-20 md:font-size-26 block md:hidden">
          {t('plan_list.component.button.available_date')}
        </p>
        <div className="calendar-info-block h-fit bg-white w-full md:relative md:sticky md:mt-0">
          <div className="calendar-info-content mb-4 md:mb-0">
            <SearchCalendar onChange={fetchData} />
          </div>
        </div>
      </div>
    </div>
  )
}

const PlanSkeleton = () => {
  return (
    <>
      <div className="mb-2 animate-pulse-card">
        <div className="h-4 w-32 bg-gray-200 rounded-sm mb-4"></div>
        <div className="flex gap-5 items-center justify-start">
          <div className="w-[120px] h-[80px] bg-gray-200 rounded-sm"></div>
          <div className="flex-1">
            <div className="h-4 w-40 bg-gray-200 rounded-sm mb-2"></div>
            <div className="h-3 w-64 bg-gray-200 rounded-sm mb-3"></div>
            <div className="flex gap-2 mt-2">
              <div className="h-6 w-24 bg-gray-200 rounded-sm"></div>
              <div className="h-6 w-20 bg-gray-200 rounded-sm"></div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 overflow-auto mt-5 mb-6">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="flex flex-col gap-2 items-center justify-between p-3 text-center w-18 h-24 bg-gray-200 rounded-md"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="h-3 w-10 bg-gray-300 rounded-sm"></div>
              <div className="h-5 w-5 bg-gray-300 rounded-full"></div>
              <div className="h-3 w-12 bg-gray-300 rounded-sm"></div>
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
